/**
 * Local media cache, page side.
 *
 * The service worker stores whatever passes through it; this is the
 * walker that makes the rest of the asset pass through it too, during
 * the gaps where the link is otherwise idle. Between them, a viewer who
 * stays a few minutes ends up with the whole programme on disk: every
 * seek lands instantly, every angle has local material, and the network
 * stops mattering.
 *
 * The walker yields to playback without exception. It fetches nothing
 * while the picture is short of buffer, mid-seek or mid-switch, so the
 * cache can never be the reason a frame is late.
 */

import type { ViewerManifest } from './api.ts';
import { canonicalKey, mediaCacheName } from './media-key.ts';
import { parsePlaylist } from './playlist.ts';
import {
  chooseNext,
  isHuntComplete,
  type CacheItem,
  type HuntWindow,
  type PrefetchState,
} from './prefetch-order.ts';

/** How long a closed gate is re-tested before the walker looks again. */
const GATE_POLL_MS = 1000;
/** Readout cadence, and how often disk truth is re-read. */
const STATS_INTERVAL_MS = 2000;
/**
 * Playback lookahead the active picture must hold before the walker
 * takes any bandwidth. Well above hls.js's own 30 s target, so the
 * walker only ever works in genuine slack.
 */
const BUFFER_FLOOR_SECONDS = 20;
/** A worker that does not answer this quickly is treated as absent. */
const HANDSHAKE_TIMEOUT_MS = 5000;
/** First load installs the worker; control arrives shortly after. */
const CONTROLLER_TIMEOUT_MS = 15_000;
/** Attempts per file before it is abandoned for this sitting. */
const MAX_ATTEMPTS = 3;
/** Signatures are refreshed at most this often, whatever fails. */
const REFRESH_INTERVAL_MS = 30_000;
/**
 * A jump shorter than two segments is a nudge, not a hunt: the player's
 * own buffer covers it and reordering the walk would gain nothing.
 */
const MIN_HUNT_GAP_SECONDS = 15;

export interface MediaCacheHost {
  activeAngleId(): string;
  playheadSeconds(): number;
  bufferedAheadSeconds(): number;
  paused(): boolean;
  seeking(): boolean;
  switching(): boolean;
  /** Mints fresh signatures when the six-hour URLs expire mid-sitting. */
  refreshManifest(): Promise<ViewerManifest>;
}

export interface CacheStats {
  cached: number;
  total: number;
  /** Whole per cent, floored, so 100 means genuinely complete. */
  percent: number;
}

/**
 * Registration is deliberately unconditional and unawaited: the worker
 * takes control when it is ready and the player never waits for it. A
 * first visit therefore streams exactly as it does today, and caching
 * begins the moment control arrives.
 */
export function registerMediaCache(): void {
  if (!import.meta.env.PROD || !('serviceWorker' in navigator) || !window.isSecureContext) {
    return;
  }
  void navigator.serviceWorker.register('/sw.js').catch(() => {
    // A worker that will not install simply is not there; every
    // request then behaves exactly as it did before this existed.
  });
}

/**
 * Whether the readout may claim a place in the tape label, answered
 * synchronously so the line is composed once and never reflows.
 *
 * The test is deliberately stricter than "a cache is possible": it
 * requires a worker already in control, which is the one condition
 * under which the cache is certain to start promptly. A first-ever
 * visit installs the worker and therefore shows no readout — the cache
 * still fills, silently, and the figure is there from the next visit
 * on, which is also the first time it has anything to report.
 */
export function mediaCacheReady(manifest: ViewerManifest): boolean {
  return (
    import.meta.env.PROD &&
    'serviceWorker' in navigator &&
    window.isSecureContext &&
    navigator.serviceWorker.controller !== null &&
    manifest.createdAt !== undefined &&
    !connectionSaysNo()
  );
}

/** Whether the cache may run at all, readout or no readout. */
function mediaCacheAvailable(): boolean {
  return (
    import.meta.env.PROD &&
    'serviceWorker' in navigator &&
    window.isSecureContext &&
    !connectionSaysNo()
  );
}

/**
 * A metered or very slow link is the one case where filling a disk is
 * hostile rather than helpful. Write-through still happens — those
 * bytes were being fetched anyway — but nothing is walked.
 */
function connectionSaysNo(): boolean {
  const connection = (
    navigator as Navigator & {
      connection?: { saveData?: boolean; effectiveType?: string };
    }
  ).connection;
  if (!connection) {
    return false;
  }
  if (connection.saveData === true) {
    return true;
  }
  return connection.effectiveType === '2g' || connection.effectiveType === 'slow-2g';
}

export class MediaCache {
  private readonly assetId: string;
  private readonly host: MediaCacheHost;
  private readonly abort = new AbortController();
  private readonly onMessage: (event: MessageEvent) => void;

  private generation: string;
  private items: CacheItem[] = [];
  private itemKeys = new Set<string>();
  private angleIds: string[] = [];
  private cached = new Set<string>();
  private skipped = new Set<string>();
  private attempts = new Map<string, number>();

  private hunt: HuntWindow | null = null;
  private turn: 'backward' | 'forward' = 'backward';
  private audioReleased = false;
  private stopped = false;
  private storageFull = false;
  private lastRefreshAt = 0;
  private statsTimer: number | null = null;

  /** Notified on every readout tick; the sole UI surface of the cache. */
  onStats: ((stats: CacheStats) => void) | null = null;

  private constructor(assetId: string, generation: string, host: MediaCacheHost) {
    this.assetId = assetId;
    this.generation = generation;
    this.host = host;
    this.onMessage = (event: MessageEvent) => {
      const data = event.data as { type?: unknown } | null;
      if (data && data.type === 'eternally:storage-full') {
        // The disk is full. Whatever is already stored keeps serving;
        // the walker stops rather than retrying a refusal.
        this.storageFull = true;
      }
    };
  }

  /**
   * Returns null whenever a local cache cannot be run honestly: no
   * worker, no control, or a manifest without an ingest generation to
   * version the copy against. In every one of those cases the player
   * behaves exactly as it did before the cache existed.
   */
  static async start(manifest: ViewerManifest, host: MediaCacheHost): Promise<MediaCache | null> {
    if (!mediaCacheAvailable() || !manifest.createdAt) {
      return null;
    }
    const controller = await awaitController(CONTROLLER_TIMEOUT_MS);
    if (!controller) {
      return null;
    }

    // Every angle is walked, on every engine. Safari plays HLS through
    // the media element rather than hls.js, and whether WebKit routes
    // those loads through a service worker was the one open question in
    // the design; measured on Safari 17.6 it does, declaring them with
    // destination "video" — playlist and segments alike. There is
    // therefore no engine on which video would be fetched and never
    // read back. See docs/LOCAL-MEDIA-CACHE.md.
    const cache = new MediaCache(manifest.assetId, manifest.createdAt, host);
    if (!(await cache.announce())) {
      return null;
    }
    if (!(await cache.rebuild(manifest))) {
      return null;
    }
    navigator.serviceWorker.addEventListener('message', cache.onMessage);
    await cache.sync();
    cache.statsTimer = window.setInterval(() => {
      void cache.sync();
    }, STATS_INTERVAL_MS);
    void cache.walk();
    return cache;
  }

  /**
   * Audio tracks are walked only once the engine's own sequential load
   * has finished, so the two never fetch the same file at once. In
   * practice the engine has already cached them all by then and this
   * phase is a verification sweep that finds nothing to do.
   */
  releaseAudio(): void {
    this.audioReleased = true;
  }

  /**
   * A forward jump means the viewer is searching, and the region they
   * skipped becomes the priority: closed from the target backwards and
   * from the departure point forwards at the same time. A backward jump
   * needs no special case — the baseline order already starts at the
   * new playhead and runs to the end.
   */
  noteSeek(originSeconds: number, targetSeconds: number): void {
    if (targetSeconds - originSeconds < MIN_HUNT_GAP_SECONDS) {
      return;
    }
    this.hunt = { originSeconds, targetSeconds };
    this.turn = 'backward';
  }

  stop(): void {
    if (this.stopped) {
      return;
    }
    this.stopped = true;
    this.abort.abort();
    if (this.statsTimer !== null) {
      window.clearInterval(this.statsTimer);
      this.statsTimer = null;
    }
    navigator.serviceWorker.removeEventListener('message', this.onMessage);
  }

  get stats(): CacheStats {
    const total = this.itemKeys.size;
    const cached = this.cached.size;
    return {
      cached,
      total,
      percent: total === 0 ? 0 : Math.floor((cached / total) * 100),
    };
  }

  /* Internals. */

  private state(): PrefetchState {
    const unavailable = new Set(this.cached);
    for (const key of this.skipped) {
      unavailable.add(key);
    }
    return {
      angleIds: this.angleIds,
      activeAngleId: this.host.activeAngleId(),
      playheadSeconds: this.host.playheadSeconds(),
      hunt: this.hunt,
      turn: this.turn,
      unavailable,
    };
  }

  /** Items the walker may consider right now. */
  private available(): CacheItem[] {
    return this.audioReleased ? this.items : this.items.filter((item) => item.kind !== 'track');
  }

  private async walk(): Promise<void> {
    while (!this.stopped && !this.storageFull) {
      const items = this.available();
      if (this.hunt && isHuntComplete(items, this.state())) {
        this.hunt = null;
      }
      const item = chooseNext(items, this.state());
      if (!item) {
        // Nothing left to fetch. The stats timer keeps the readout
        // honest if the player writes anything through later.
        return;
      }
      if (!(await this.gate())) {
        return;
      }
      await this.take(item);
      this.turn = this.turn === 'backward' ? 'forward' : 'backward';
    }
  }

  /** Blocks until playback has slack to spare, or the walker stops. */
  private async gate(): Promise<boolean> {
    while (!this.stopped) {
      if (this.host.switching() || this.host.seeking()) {
        // A switch or a seek is exactly when the link is needed most.
      } else if (this.host.paused() || this.host.bufferedAheadSeconds() >= BUFFER_FLOOR_SECONDS) {
        return true;
      }
      await sleep(GATE_POLL_MS, this.abort.signal);
    }
    return false;
  }

  private async take(item: CacheItem): Promise<void> {
    if (await this.onDisk(item.key)) {
      this.cached.add(item.key);
      return;
    }
    try {
      const response = await fetch(item.url, {
        credentials: 'same-origin',
        signal: this.abort.signal,
      });
      if (response.status === 403) {
        // The six-hour signature has expired mid-sitting.
        const refreshed = await this.refresh();
        this.fail(item.key);
        if (!refreshed) {
          this.stop();
        }
        return;
      }
      if (!response.ok) {
        this.fail(item.key);
        return;
      }
      // The body must be drained for the download to complete and for
      // the worker's clone to reach storage.
      await response.arrayBuffer();
      this.cached.add(item.key);
    } catch {
      if (!this.stopped) {
        this.fail(item.key);
      }
    }
  }

  private fail(key: string): void {
    const count = (this.attempts.get(key) ?? 0) + 1;
    this.attempts.set(key, count);
    if (count >= MAX_ATTEMPTS) {
      // Abandoned for this sitting. It stays counted in the total, so
      // the readout tells the truth rather than reaching a false 100 %.
      this.skipped.add(key);
    }
  }

  private async onDisk(key: string): Promise<boolean> {
    try {
      const cache = await caches.open(mediaCacheName(this.assetId));
      return (await cache.match(key)) !== undefined;
    } catch {
      return false;
    }
  }

  /** Re-reads disk truth, so write-through by the player counts too. */
  private async sync(): Promise<void> {
    try {
      const cache = await caches.open(mediaCacheName(this.assetId));
      const keys = await cache.keys();
      const present = new Set<string>();
      for (const request of keys) {
        const key = canonicalKey(request.url);
        if (this.itemKeys.has(key)) {
          present.add(key);
        }
      }
      this.cached = present;
    } catch {
      // A failed read leaves the previous figure standing.
    }
    this.onStats?.(this.stats);
  }

  private async announce(): Promise<boolean> {
    const controller = navigator.serviceWorker.controller;
    if (!controller) {
      return false;
    }
    const assetId = this.assetId;
    const generation = this.generation;
    return new Promise<boolean>((resolve) => {
      const channel = new MessageChannel();
      const timer = window.setTimeout(() => {
        channel.port1.close();
        resolve(false);
      }, HANDSHAKE_TIMEOUT_MS);
      channel.port1.onmessage = (event: MessageEvent) => {
        window.clearTimeout(timer);
        const data = event.data as { ok?: unknown; storageFull?: unknown } | null;
        if (data?.storageFull === true) {
          this.storageFull = true;
        }
        channel.port1.close();
        resolve(data?.ok === true);
      };
      controller.postMessage({ type: 'eternally:asset', assetId, generation }, [channel.port2]);
    });
  }

  /**
   * Rebuild the file list from a manifest. Every item is identified by
   * its canonical key, which survives re-signing untouched, so a
   * refresh replaces URLs without losing a byte of progress.
   */
  private async rebuild(manifest: ViewerManifest): Promise<boolean> {
    const angles =
      manifest.video.angles && manifest.video.angles.length > 0
        ? manifest.video.angles
        : [
            {
              id: 'main',
              label: 'Programme',
              playlistUrl: manifest.video.playlistUrl,
              ...(manifest.video.posterUrl ? { posterUrl: manifest.video.posterUrl } : {}),
            },
          ];

    const items: CacheItem[] = [];
    for (const angle of angles) {
      if (angle.posterUrl) {
        items.push({
          kind: 'poster',
          key: keyOf(angle.posterUrl),
          url: angle.posterUrl,
          angleId: angle.id,
        });
      }
      const segments = await this.readPlaylist(angle.playlistUrl);
      if (!segments) {
        // An unreadable playlist costs this angle its local copy and
        // nothing else; playback never consults this list.
        continue;
      }
      for (const segment of segments) {
        items.push({
          kind: 'segment',
          key: keyOf(segment.url),
          url: segment.url,
          angleId: angle.id,
          index: segment.index,
          startSeconds: segment.startSeconds,
          endSeconds: segment.endSeconds,
        });
      }
    }
    for (const track of manifest.tracks) {
      items.push({ kind: 'track', key: keyOf(track.url), url: track.url, trackId: track.id });
    }

    if (items.length === 0) {
      return false;
    }
    this.items = items;
    this.itemKeys = new Set(items.map((item) => item.key));
    this.angleIds = angles.map((angle) => angle.id);
    return true;
  }

  private async readPlaylist(playlistUrl: string): Promise<ReturnType<typeof parsePlaylist> | null> {
    try {
      const response = await fetch(playlistUrl, {
        credentials: 'same-origin',
        signal: this.abort.signal,
      });
      if (!response.ok) {
        return null;
      }
      return parsePlaylist(await response.text());
    } catch {
      return null;
    }
  }

  /**
   * Mint fresh signatures. If the asset has been re-ingested in the
   * meantime the generation has moved, and the worker is told at once
   * so the stale copy is discarded rather than mixed into playback.
   */
  private async refresh(): Promise<boolean> {
    const now = Date.now();
    if (now - this.lastRefreshAt < REFRESH_INTERVAL_MS) {
      return true;
    }
    this.lastRefreshAt = now;
    try {
      const fresh = await this.host.refreshManifest();
      if (!fresh.createdAt) {
        return false;
      }
      if (fresh.createdAt !== this.generation) {
        this.generation = fresh.createdAt;
        this.cached = new Set();
        this.skipped = new Set();
        this.attempts = new Map();
        if (!(await this.announce())) {
          return false;
        }
      }
      return await this.rebuild(fresh);
    } catch {
      return false;
    }
  }
}

function keyOf(url: string): string {
  return canonicalKey(new URL(url, window.location.origin));
}

function awaitController(timeoutMs: number): Promise<ServiceWorker | null> {
  if (navigator.serviceWorker.controller) {
    return Promise.resolve(navigator.serviceWorker.controller);
  }
  return new Promise((resolve) => {
    const settle = (value: ServiceWorker | null) => {
      window.clearTimeout(timer);
      navigator.serviceWorker.removeEventListener('controllerchange', onChange);
      resolve(value);
    };
    const onChange = () => settle(navigator.serviceWorker.controller);
    const timer = window.setTimeout(() => settle(null), timeoutMs);
    navigator.serviceWorker.addEventListener('controllerchange', onChange);
  });
}

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal.aborted) {
      resolve();
      return;
    }
    const done = () => {
      window.clearTimeout(timer);
      signal.removeEventListener('abort', done);
      resolve();
    };
    const timer = window.setTimeout(done, ms);
    signal.addEventListener('abort', done, { once: true });
  });
}
